import React, { useCallback, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useQueryClient, UseMutateFunction } from "@tanstack/react-query";
import { convertFormToInsertInvoice } from "@/utils/invoiceUtils";
import { InsertInvoiceDto, BankAccountDto, InvoiceDetailsDto, InvoiceFormData } from "@/types";

type UpdateInvoiceByPutMutation = {
    id: string;
    invoice: InsertInvoiceDto;
};

type UseAutosaveProps = {
    isFormChanged: boolean;
    bankAccounts: BankAccountDto[];
    updateInvoice: UseMutateFunction<string, Error, UpdateInvoiceByPutMutation, unknown>;
    isUpdatingInvoice: boolean;
    clearFormChangedIfCurrent: (savedVersion: number) => void;
    formChangeVersionRef: React.RefObject<number>;
    lastSavedRef: React.RefObject<number>;
    invoiceId: string;
    isInvoiceInSubmission: boolean;
};

const AUTOSAVE_DEBOUNCE_MS = 800;

const mergeInvoiceData = (
    oldData: InvoiceDetailsDto | undefined,
    invoice: InsertInvoiceDto
): InvoiceDetailsDto | undefined => (oldData ? { ...oldData, ...invoice } : oldData);

const useAutosave = ({
    isFormChanged,
    bankAccounts,
    updateInvoice,
    isUpdatingInvoice,
    clearFormChangedIfCurrent,
    formChangeVersionRef,
    lastSavedRef,
    invoiceId,
    isInvoiceInSubmission
}: UseAutosaveProps) => {
    const queryClient = useQueryClient();
    const { getValues, watch } = useFormContext<InvoiceFormData>();

    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const wasUpdatingRef = useRef(false);
    const versionAtSaveStartRef = useRef(0);

    const propsRef = useRef({
        isFormChanged,
        bankAccounts,
        updateInvoice,
        isUpdatingInvoice,
        clearFormChangedIfCurrent,
        formChangeVersionRef,
        lastSavedRef,
        invoiceId,
        isInvoiceInSubmission
    });
    propsRef.current = {
        isFormChanged,
        bankAccounts,
        updateInvoice,
        isUpdatingInvoice,
        clearFormChangedIfCurrent,
        formChangeVersionRef,
        lastSavedRef,
        invoiceId,
        isInvoiceInSubmission
    };

    const saveIfNeeded = useCallback(() => {
        const {
            isFormChanged: hasChanges,
            isUpdatingInvoice: isSaving,
            isInvoiceInSubmission: isSubmitting,
            bankAccounts: accounts,
            updateInvoice: saveInvoice,
            formChangeVersionRef: changeVersionRef,
            clearFormChangedIfCurrent: clearIfCurrent,
            lastSavedRef: savedAtRef,
            invoiceId: id
        } = propsRef.current;

        if (!hasChanges || isSubmitting || isSaving) {
            return;
        }

        const formData = getValues();
        const bankAccount = accounts.find(
            e =>
                e.accountNumber === formData.bankAccount?.accountNumber && e.currency === formData.bankAccount?.currency
        );
        const invoice: InsertInvoiceDto = convertFormToInsertInvoice(formData, bankAccount);
        const savedVersion = changeVersionRef.current;

        versionAtSaveStartRef.current = savedVersion;

        saveInvoice(
            { id, invoice },
            {
                onSuccess: () => {
                    clearIfCurrent(savedVersion);
                    savedAtRef.current = Date.now();
                    queryClient.setQueryData<InvoiceDetailsDto>(["invoice", id], oldData =>
                        mergeInvoiceData(oldData, invoice)
                    );
                }
            }
        );
    }, [getValues, queryClient]);

    const scheduleDebouncedSave = useCallback(() => {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(saveIfNeeded, AUTOSAVE_DEBOUNCE_MS);
    }, [saveIfNeeded]);

    useEffect(() => {
        const subscription = watch(() => {
            scheduleDebouncedSave();
        });

        return () => {
            subscription.unsubscribe();
            clearTimeout(debounceTimerRef.current);
        };
    }, [watch, scheduleDebouncedSave]);

    useEffect(() => {
        const wasUpdating = wasUpdatingRef.current;
        wasUpdatingRef.current = isUpdatingInvoice;

        const hasNewChangesSinceSave = formChangeVersionRef.current !== versionAtSaveStartRef.current;

        if (wasUpdating && !isUpdatingInvoice && !isInvoiceInSubmission && hasNewChangesSinceSave) {
            saveIfNeeded();
        }
    }, [isUpdatingInvoice, isInvoiceInSubmission, formChangeVersionRef, saveIfNeeded]);
};

export default useAutosave;
